import React, { useEffect, useState } from "react";
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Typography,
    Avatar,
    Pagination,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../components/Loader/Loading";
import notfound from "../assets/images/not-found.png";
import { ChangeReuestStatus, GetWithdrawRequestsList } from "../Redux/Actions/WithdrawalAction";
import { toast } from "react-toastify";
import ConfirmWithdrawReqModal from "../Models/ConfirmWithdrawReqModal";
import { PiDotsThreeOutlineVerticalFill } from "react-icons/pi";

const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (date.toDateString() === today.toDateString())
        return `Today at ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
    if (date.toDateString() === yesterday.toDateString())
        return `Yesterday at ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;

    return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
};

const getStatusDetails = (status) => {
    const map = {
        approved: {
            bg: 'bg-green-100 text-green-600',
            dot: 'bg-green-500 animate-pulse',
            text: 'Approved',
        },
        pending: {
            bg: 'bg-yellow-100 text-yellow-600',
            dot: 'bg-yellow-500 animate-pulse',
            text: 'Pending',
        },
        rejected: {
            bg: 'bg-red-100 text-red-600',
            dot: 'bg-red-500 animate-pulse',
            text: 'Rejected',
        },
    };
    return map[status?.toLowerCase()] || {
        bg: 'bg-gray-100 text-gray-600',
        dot: 'bg-gray-400',
        text: 'Unknown',
    };
};

const WithdrawalRequests = () => {
    const dispatch = useDispatch();
    const [page, setPage] = useState(1);
    const [selectedRequest, setSelectedRequest] = useState(null);

    const rowsPerPage = 10;
    const { withdrawalRequests, loading } = useSelector((state) => state.Withdraw);


    useEffect(() => {
        dispatch(GetWithdrawRequestsList(page));
    }, [dispatch, page]);

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleRequest = async (status, id) => {
        try {
            const res = await dispatch(ChangeReuestStatus(status, id));
            if (res) {
                toast.success("Status updated successfully!");
                dispatch(GetWithdrawRequestsList(page));
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to update status");
        } finally {
            setSelectedRequest(null);
        }
    };


    return (
        <>
            <Box sx={{ padding: 2 }}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        justifyContent: "space-between",
                        alignItems: { xs: "flex-start", md: "center" },
                        mb: 2,
                        gap: 1,
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: "bold",
                            color: "#424242",
                        }}
                    >
                        Withdrawal Requests
                    </Typography>
                </Box>

                <div className="w-full overflow-x-auto z-[0]">
                    <Paper sx={{ height: "auto", background: "transparent" }}>
                        <TableContainer sx={{ minHeight: "78vh", align: "center" }} className="relative">
                            <Table aria-label="sticky table">
                                <TableHead>
                                    <TableRow className="!font-bold">
                                        <TableCell align="left" className="!font-semibold" sx={{ padding: "12px" }}>
                                            Name
                                        </TableCell>
                                        <TableCell align="left" className="!font-semibold" sx={{ padding: "12px" }}>
                                            Amount
                                        </TableCell>
                                        <TableCell align="left" className="!font-semibold" sx={{ padding: "12px" }}>
                                            Requested At
                                        </TableCell>
                                        <TableCell align="left" className="!font-semibold" sx={{ padding: "12px" }}>
                                            Status / Action
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading ? (
                                        <Loader />
                                    ) : withdrawalRequests?.data?.length > 0 ? (
                                        withdrawalRequests.data.map((obj) => {
                                            const status = getStatusDetails(obj.status);
                                            return <TableRow key={obj.id}>
                                                <TableCell align="left" sx={{ padding: "12px" }}>
                                                    <div className="flex items-center gap-2">
                                                        <Avatar src={obj?.User?.profileImage} />
                                                        <span className="text-nowrap">
                                                            {obj?.User?.first_name} {obj?.User?.last_name}
                                                        </span>
                                                    </div>
                                                </TableCell>

                                                <TableCell align="left" sx={{ padding: "12px" }}>
                                                    <span>${obj?.amount}</span>
                                                </TableCell>

                                                <TableCell align="left" sx={{ padding: "12px" }}>
                                                    <span>{formatDate(obj?.created_at)}</span>
                                                </TableCell>

                                                <TableCell align="left" sx={{ padding: "12px" }}>
                                                    <div className="flex items-center gap-2 min-w-[140px]">
                                                        <span
                                                            className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${status.bg}`}
                                                        >
                                                            <span className={`w-2 h-2 rounded-full ${status.dot}`}></span>
                                                            {status.text}

                                                        </span>
                                                        {obj.status === "pending" && (
                                                            <IconButton onClick={() => setSelectedRequest(obj)}>
                                                                <PiDotsThreeOutlineVerticalFill size={16} />
                                                            </IconButton>
                                                        )}

                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        })
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={4}>
                                                <div className="flex flex-col justify-center items-center w-full h-[60vh]">
                                                    <img src={notfound} alt="Not Found" style={{ width: "200px" }} />
                                                    <span className="text-xl font-bold mt-6">No data found</span>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <div className="flex justify-end pt-3">
                            <Pagination
                                shape="rounded"
                                count={Math.ceil(withdrawalRequests?.pagination?.totalRecords / rowsPerPage)}
                                page={page}
                                onChange={handlePageChange}
                                sx={{
                                    "& .MuiPaginationItem-root.Mui-selected": {
                                        background: "linear-gradient(to right, #EF5A24, #F38157)",
                                        color: "#fff",
                                    },
                                    "@media (max-width: 960px)": {
                                        fontSize: "0.75rem",
                                        "& .MuiPaginationItem-root": {
                                            minWidth: "auto",
                                            padding: "8px",
                                            margin: "2px",
                                        },
                                    },
                                    "@media (max-width: 600px)": {
                                        fontSize: "0.5rem",
                                        "& .MuiPaginationItem-root": {
                                            padding: "8px",
                                            margin: "1px",
                                        },
                                    },
                                }}
                            />
                        </div>
                    </Paper>
                </div>
            </Box>
            {selectedRequest && (
                <ConfirmWithdrawReqModal
                    open={Boolean(selectedRequest)}
                    onClose={() => setSelectedRequest(null)}
                    data={selectedRequest}
                    handleRequest={handleRequest}
                />
            )}

        </>
    );
};

export default WithdrawalRequests;